/**
 * `sniffMedia` (W1-07b formats-package; contract formats/types.ts `SniffMediaFn`; formats-and-packaging.md P§2.7)
 * and `probeMedia` (asset metadata: duration, sample rate, channels, image size, GIF frames).
 *
 * The content decides, never the file name alone (a ".mp3" that is really Opus-in-Ogg is Opus); the name only
 * breaks ties the bytes cannot settle (text kinds, Matroska audio vs video, an MP4 without its index).
 *
 * Phira decodes (P§2.7): audio MP3 (MPEG layer III), Ogg Vorbis, WAV (PCM, float, ADPCM), FLAC, AAC in ADTS
 * frames; NOT Opus, Ogg FLAC / Speex, M4A / MP4 audio, Matroska audio. Images PNG, JPEG (baseline / progressive),
 * WebP, BMP, GIF; NOT AVIF, PPM / netpbm, TIFF, ICO, SVG, HEIC. Video MP4 / MOV / WebM / Matroska / AVI / Ogg
 * Theora through FFmpeg (H.264 MP4 is the safe choice). RPE offers MP3 / WAV / OGG and PNG / JPEG only.
 *
 * `probeMedia` reads durations from the containers without decoding: WAV (data size / byte rate, or the `fact`
 * sample count), FLAC (STREAMINFO), Ogg (last granule position; Opus pre-skip at 48 kHz), MP3 (Xing / Info frame
 * count, else a walk over every frame header), AAC ADTS (frame walk), MP4 / M4A / MOV (`mvhd`).
 */

import { gifDelaysMs, gifTotalMs } from '../../core/image/gifDelays';
import type { AssetMeta } from '../../core/model/types';
import { sniffImage } from '../respack/validate';
import type { MediaSniff, SniffMediaFn } from '../types';

function u8(b: Uint8Array, i: number): number {
  return b[i] ?? 0;
}
function u16le(b: Uint8Array, i: number): number {
  return u8(b, i) | (u8(b, i + 1) << 8);
}
function u32le(b: Uint8Array, i: number): number {
  return (u8(b, i) | (u8(b, i + 1) << 8) | (u8(b, i + 2) << 16) | (u8(b, i + 3) << 24)) >>> 0;
}
function u32be(b: Uint8Array, i: number): number {
  return ((u8(b, i) << 24) | (u8(b, i + 1) << 16) | (u8(b, i + 2) << 8) | u8(b, i + 3)) >>> 0;
}
function u64be(b: Uint8Array, i: number): number {
  return u32be(b, i) * 0x100000000 + u32be(b, i + 4);
}
function i64le(b: Uint8Array, i: number): number {
  const lo = u32le(b, i);
  const hi = u32le(b, i + 4) | 0;
  return hi * 0x100000000 + lo;
}
function ascii(b: Uint8Array, i: number, s: string): boolean {
  if (i < 0 || i + s.length > b.length) return false;
  for (let k = 0; k < s.length; k++) if (b[i + k] !== s.charCodeAt(k)) return false;
  return true;
}

function extOf(name: string): string {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

// ---------------------------------------------------------------------------------------------------------------
// Codec table
// ---------------------------------------------------------------------------------------------------------------

interface CodecInfo {
  readonly mime: string;
  readonly kind: MediaSniff['kind'];
  readonly phira: boolean;
  readonly rpe: boolean;
}

const CODECS: Readonly<Record<string, CodecInfo>> = {
  mp3: { mime: 'audio/mpeg', kind: 'audio', phira: true, rpe: true },
  mp2: { mime: 'audio/mpeg', kind: 'audio', phira: false, rpe: false },
  'ogg-vorbis': { mime: 'audio/ogg', kind: 'audio', phira: true, rpe: true },
  opus: { mime: 'audio/ogg', kind: 'audio', phira: false, rpe: false },
  'ogg-flac': { mime: 'audio/ogg', kind: 'audio', phira: false, rpe: false },
  'ogg-speex': { mime: 'audio/ogg', kind: 'audio', phira: false, rpe: false },
  ogg: { mime: 'audio/ogg', kind: 'audio', phira: false, rpe: false },
  wav: { mime: 'audio/wav', kind: 'audio', phira: true, rpe: true },
  'wav-unsupported': { mime: 'audio/wav', kind: 'audio', phira: false, rpe: false },
  flac: { mime: 'audio/flac', kind: 'audio', phira: true, rpe: false },
  aac: { mime: 'audio/aac', kind: 'audio', phira: true, rpe: false },
  m4a: { mime: 'audio/mp4', kind: 'audio', phira: false, rpe: false },
  mka: { mime: 'audio/x-matroska', kind: 'audio', phira: false, rpe: false },
  weba: { mime: 'audio/webm', kind: 'audio', phira: false, rpe: false },
  png: { mime: 'image/png', kind: 'image', phira: true, rpe: true },
  jpg: { mime: 'image/jpeg', kind: 'image', phira: true, rpe: true },
  'jpg-unsupported': { mime: 'image/jpeg', kind: 'image', phira: false, rpe: false },
  webp: { mime: 'image/webp', kind: 'image', phira: true, rpe: false },
  bmp: { mime: 'image/bmp', kind: 'image', phira: true, rpe: false },
  gif: { mime: 'image/gif', kind: 'image', phira: true, rpe: false },
  avif: { mime: 'image/avif', kind: 'image', phira: false, rpe: false },
  heic: { mime: 'image/heic', kind: 'image', phira: false, rpe: false },
  ppm: { mime: 'image/x-portable-anymap', kind: 'image', phira: false, rpe: false },
  tiff: { mime: 'image/tiff', kind: 'image', phira: false, rpe: false },
  ico: { mime: 'image/x-icon', kind: 'image', phira: false, rpe: false },
  svg: { mime: 'image/svg+xml', kind: 'image', phira: false, rpe: false },
  mp4: { mime: 'video/mp4', kind: 'video', phira: true, rpe: false },
  mov: { mime: 'video/quicktime', kind: 'video', phira: true, rpe: false },
  webm: { mime: 'video/webm', kind: 'video', phira: true, rpe: false },
  mkv: { mime: 'video/x-matroska', kind: 'video', phira: true, rpe: false },
  avi: { mime: 'video/x-msvideo', kind: 'video', phira: true, rpe: false },
  ogv: { mime: 'video/ogg', kind: 'video', phira: true, rpe: false },
  glsl: { mime: 'text/plain', kind: 'text', phira: true, rpe: false },
  json: { mime: 'application/json', kind: 'text', phira: true, rpe: false },
  yaml: { mime: 'application/yaml', kind: 'text', phira: true, rpe: false },
  pec: { mime: 'text/plain', kind: 'text', phira: true, rpe: false },
  text: { mime: 'text/plain', kind: 'text', phira: true, rpe: false },
  ttf: { mime: 'font/ttf', kind: 'other', phira: false, rpe: false },
  otf: { mime: 'font/otf', kind: 'other', phira: false, rpe: false },
  woff: { mime: 'font/woff', kind: 'other', phira: false, rpe: false },
  woff2: { mime: 'font/woff2', kind: 'other', phira: false, rpe: false },
  unknown: { mime: 'application/octet-stream', kind: 'other', phira: false, rpe: false },
};

function result(codec: string): MediaSniff {
  const info = CODECS[codec] ?? CODECS.unknown;
  const c = info ?? { mime: 'application/octet-stream', kind: 'other' as const, phira: false, rpe: false };
  return { mime: c.mime, codec, kind: c.kind, phiraDecodable: c.phira, rpeFriendly: c.rpe };
}

// ---------------------------------------------------------------------------------------------------------------
// Container detection
// ---------------------------------------------------------------------------------------------------------------

/** Offset just after an ID3v2 tag at the start (0 when there is none). */
function id3v2End(b: Uint8Array): number {
  if (!ascii(b, 0, 'ID3') || b.length < 10) return 0;
  const size = ((u8(b, 6) & 0x7f) << 21) | ((u8(b, 7) & 0x7f) << 14) | ((u8(b, 8) & 0x7f) << 7) | (u8(b, 9) & 0x7f);
  const footer = (u8(b, 5) & 0x10) !== 0 ? 10 : 0;
  return 10 + size + footer;
}

function isAdtsHeader(b: Uint8Array, i: number): boolean {
  return u8(b, i) === 0xff && (u8(b, i + 1) & 0xf6) === 0xf0 && ((u8(b, i + 2) >> 2) & 0x0f) < 13;
}

interface MpegFrame {
  readonly layer: 1 | 2 | 3;
  readonly mpeg1: boolean;
  readonly sampleRate: number;
  readonly length: number;
  readonly samples: number;
  readonly channels: number;
}

const MPEG_RATES: readonly (readonly number[])[] = [
  [11025, 12000, 8000], // MPEG 2.5
  [], // reserved
  [22050, 24000, 16000], // MPEG 2
  [44100, 48000, 32000], // MPEG 1
];
const BITRATES_V1_L3 = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
const BITRATES_V1_L2 = [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384];
const BITRATES_V1_L1 = [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448];
const BITRATES_V2_L1 = [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256];
const BITRATES_V2_L23 = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160];

/** MPEG audio frame header at `i`, or null. */
function mpegFrame(b: Uint8Array, i: number): MpegFrame | null {
  const b1 = u8(b, i + 1);
  const b2 = u8(b, i + 2);
  if (u8(b, i) !== 0xff || (b1 & 0xe0) !== 0xe0) return null;
  const version = (b1 >> 3) & 3;
  const layerBits = (b1 >> 1) & 3;
  if (version === 1 || layerBits === 0) return null;
  const layer = (4 - layerBits) as 1 | 2 | 3;
  const bitrateIndex = (b2 >> 4) & 0x0f;
  const rateIndex = (b2 >> 2) & 3;
  if (bitrateIndex === 0 || bitrateIndex === 15 || rateIndex === 3) return null;
  const mpeg1 = version === 3;
  const table = mpeg1
    ? layer === 3
      ? BITRATES_V1_L3
      : layer === 2
        ? BITRATES_V1_L2
        : BITRATES_V1_L1
    : layer === 1
      ? BITRATES_V2_L1
      : BITRATES_V2_L23;
  const bitrate = (table[bitrateIndex] ?? 0) * 1000;
  const sampleRate = MPEG_RATES[version]?.[rateIndex] ?? 0;
  if (bitrate === 0 || sampleRate === 0) return null;
  const padding = (b2 >> 1) & 1;
  const samples = layer === 1 ? 384 : layer === 2 ? 1152 : mpeg1 ? 1152 : 576;
  const length =
    layer === 1
      ? Math.floor((12 * bitrate) / sampleRate + padding) * 4
      : Math.floor(((samples / 8) * bitrate) / sampleRate) + padding;
  const channels = ((u8(b, i + 3) >> 6) & 3) === 3 ? 1 : 2;
  return length >= 4 ? { layer, mpeg1, sampleRate, length, samples, channels } : null;
}

function oggCodec(b: Uint8Array): string {
  const segments = u8(b, 26);
  const p = 27 + segments;
  if (u8(b, p) === 0x01 && ascii(b, p + 1, 'vorbis')) return 'ogg-vorbis';
  if (ascii(b, p, 'OpusHead')) return 'opus';
  if (u8(b, p) === 0x7f && ascii(b, p + 1, 'FLAC')) return 'ogg-flac';
  if (ascii(b, p, 'Speex   ')) return 'ogg-speex';
  if (u8(b, p) === 0x80 && ascii(b, p + 1, 'theora')) return 'ogv';
  return 'ogg';
}

/** RIFF chunks of a WAVE file: [id, data start, size]. */
function riffChunks(b: Uint8Array): Array<readonly [string, number, number]> {
  const out: Array<readonly [string, number, number]> = [];
  let p = 12;
  while (p + 8 <= b.length) {
    const id = String.fromCharCode(u8(b, p), u8(b, p + 1), u8(b, p + 2), u8(b, p + 3));
    const size = u32le(b, p + 4);
    out.push([id, p + 8, size]);
    p += 8 + size + (size & 1);
  }
  return out;
}

/** WAVE format tags symphonia's RIFF reader decodes: PCM, IEEE float, MS ADPCM, IMA ADPCM, A-law, mu-law, extensible. */
const WAV_DECODABLE: ReadonlySet<number> = new Set([0x0001, 0x0003, 0x0002, 0x0011, 0x0006, 0x0007, 0xfffe]);

function wavCodec(b: Uint8Array): string {
  const fmt = riffChunks(b).find(([id]) => id === 'fmt ');
  if (fmt === undefined) return 'wav-unsupported';
  return WAV_DECODABLE.has(u16le(b, fmt[1])) ? 'wav' : 'wav-unsupported';
}

/** ISO BMFF boxes inside [start, end): [type, payload start, box end]. */
function boxes(b: Uint8Array, start: number, end: number): Array<readonly [string, number, number]> {
  const out: Array<readonly [string, number, number]> = [];
  let p = start;
  while (p + 8 <= end) {
    let size = u32be(b, p);
    const type = String.fromCharCode(u8(b, p + 4), u8(b, p + 5), u8(b, p + 6), u8(b, p + 7));
    let header = 8;
    if (size === 1) {
      size = u64be(b, p + 8);
      header = 16;
    } else if (size === 0) size = end - p;
    if (size < header) break;
    out.push([type, p + header, Math.min(end, p + size)]);
    p += size;
  }
  return out;
}

function childBox(
  b: Uint8Array,
  parent: readonly [string, number, number],
  type: string,
): readonly [string, number, number] | undefined {
  return boxes(b, parent[1], parent[2]).find(([t]) => t === type);
}

/** Handler types of the tracks of an MP4 / MOV ('vide', 'soun', ...); null when there is no readable moov. */
function mp4Handlers(b: Uint8Array): string[] | null {
  const moov = boxes(b, 0, b.length).find(([t]) => t === 'moov');
  if (moov === undefined) return null;
  const out: string[] = [];
  for (const trak of boxes(b, moov[1], moov[2]).filter(([t]) => t === 'trak')) {
    const mdia = childBox(b, trak, 'mdia');
    const hdlr = mdia === undefined ? undefined : childBox(b, mdia, 'hdlr');
    if (hdlr !== undefined)
      out.push(String.fromCharCode(u8(b, hdlr[1] + 8), u8(b, hdlr[1] + 9), u8(b, hdlr[1] + 10), u8(b, hdlr[1] + 11)));
  }
  return out;
}

function ftypCodec(b: Uint8Array, ext: string): string {
  const size = Math.min(u32be(b, 0), b.length);
  const brands: string[] = [];
  for (let p = 8; p + 4 <= size; p += 4) {
    if (p === 12) continue; // minor version
    brands.push(String.fromCharCode(u8(b, p), u8(b, p + 1), u8(b, p + 2), u8(b, p + 3)));
  }
  const major = brands[0] ?? '';
  if (brands.includes('avif') || brands.includes('avis')) return 'avif';
  if (['heic', 'heix', 'hevc', 'heim', 'heis'].some((x) => brands.includes(x))) return 'heic';
  if (major === 'M4A ' || major === 'M4B ' || major === 'M4P ') return 'm4a';
  const handlers = mp4Handlers(b);
  const isMov = major === 'qt  ';
  if (handlers !== null) {
    if (handlers.includes('vide')) return isMov ? 'mov' : 'mp4';
    if (handlers.includes('soun')) return 'm4a';
  }
  if (ext === 'm4a' || ext === 'aac' || ext === 'm4b') return 'm4a';
  return isMov ? 'mov' : 'mp4';
}

function ebmlCodec(b: Uint8Array, ext: string): string {
  const head = b.subarray(0, Math.min(b.length, 64));
  let webm = false;
  for (let i = 0; i + 4 <= head.length; i++) if (ascii(head, i, 'webm')) webm = true;
  const audioExt = ext === 'mka' || ext === 'weba' || ext === 'ogg' || ext === 'opus';
  if (audioExt) return webm ? 'weba' : 'mka';
  return webm ? 'webm' : 'mkv';
}

function isUtf8Text(b: Uint8Array): boolean {
  const head = b.subarray(0, Math.min(b.length, 65536));
  for (const x of head) if (x === 0) return false;
  try {
    // A multi-byte character cut at the window end is still text.
    new TextDecoder('utf-8', { fatal: true }).decode(head.length < b.length ? head.subarray(0, head.length - 4) : head);
    return true;
  } catch {
    return false;
  }
}

const TEXT_CODEC_BY_EXT: Readonly<Record<string, string>> = {
  glsl: 'glsl',
  frag: 'glsl',
  fs: 'glsl',
  vert: 'glsl',
  shader: 'glsl',
  json: 'json',
  yml: 'yaml',
  yaml: 'yaml',
  pec: 'pec',
};

export const sniffMedia: SniffMediaFn = (bytes, name) => {
  const b = bytes;
  const ext = extOf(name);
  if (b.length >= 12 && ascii(b, 0, 'RIFF')) {
    if (ascii(b, 8, 'WAVE')) return result(wavCodec(b));
    if (ascii(b, 8, 'WEBP')) return result('webp');
    if (ascii(b, 8, 'AVI ')) return result('avi');
  }
  if (ascii(b, 0, 'OggS')) return result(oggCodec(b));
  if (ascii(b, 0, 'fLaC')) return result('flac');
  if (b.length >= 12 && ascii(b, 4, 'ftyp')) return result(ftypCodec(b, ext));
  if (u32be(b, 0) === 0x1a45dfa3) return result(ebmlCodec(b, ext));
  if (b.length >= 8 && u8(b, 0) === 0x89 && ascii(b, 1, 'PNG')) return result('png');
  if (u8(b, 0) === 0xff && u8(b, 1) === 0xd8 && u8(b, 2) === 0xff) {
    return result(sniffImage(b) === null ? 'jpg-unsupported' : 'jpg');
  }
  if (ascii(b, 0, 'GIF87a') || ascii(b, 0, 'GIF89a')) return result('gif');
  if (ascii(b, 0, 'BM') && [12, 40, 52, 56, 108, 124].includes(u32le(b, 14))) return result('bmp');
  if (ascii(b, 0, 'II*\0') || ascii(b, 0, 'MM\0*')) return result('tiff');
  if (u32le(b, 0) === 0x00010000 && ext === 'ico') return result('ico');
  if (u8(b, 0) === 0x50 && u8(b, 1) >= 0x31 && u8(b, 1) <= 0x37 && [0x20, 0x09, 0x0a, 0x0d].includes(u8(b, 2))) {
    return result('ppm');
  }
  if (ascii(b, 0, 'wOFF')) return result('woff');
  if (ascii(b, 0, 'wOF2')) return result('woff2');
  if (ascii(b, 0, 'OTTO')) return result('otf');
  if (u32be(b, 0) === 0x00010000 || ascii(b, 0, 'true')) return result('ttf');
  const id3 = id3v2End(b);
  if (isAdtsHeader(b, id3)) return result('aac');
  const frame = mpegFrame(b, id3);
  if (frame !== null) return result(frame.layer === 3 ? 'mp3' : 'mp2');
  if (id3 > 0) return result('mp3');
  if (isUtf8Text(b)) {
    const text = new TextDecoder('utf-8').decode(b.subarray(0, Math.min(b.length, 512))).trimStart();
    if (text.startsWith('<svg') || (text.startsWith('<?xml') && text.includes('<svg'))) return result('svg');
    return result(TEXT_CODEC_BY_EXT[ext] ?? 'text');
  }
  return result('unknown');
};

// ---------------------------------------------------------------------------------------------------------------
// Metadata probing
// ---------------------------------------------------------------------------------------------------------------

/** What the container headers tell about an audio, image or video file (fields left out when unknown). */
export type MediaProbe = Pick<
  AssetMeta,
  'width' | 'height' | 'durationSec' | 'sampleRate' | 'channels' | 'gifFrames' | 'gifDelaysMs' | 'gifTotalMs'
>;

type MutableProbe = { -readonly [K in keyof MediaProbe]: MediaProbe[K] };

function probeWav(b: Uint8Array, out: MutableProbe): void {
  const chunks = riffChunks(b);
  const fmt = chunks.find(([id]) => id === 'fmt ');
  const data = chunks.find(([id]) => id === 'data');
  const fact = chunks.find(([id]) => id === 'fact');
  if (fmt === undefined) return;
  const channels = u16le(b, fmt[1] + 2);
  const rate = u32le(b, fmt[1] + 4);
  const byteRate = u32le(b, fmt[1] + 8);
  if (channels > 0) out.channels = channels;
  if (rate > 0) out.sampleRate = rate;
  const format = u16le(b, fmt[1]);
  if (fact !== undefined && format !== 0x0001 && format !== 0x0003 && rate > 0) {
    out.durationSec = u32le(b, fact[1]) / rate;
  } else if (data !== undefined && byteRate > 0) {
    // A data chunk whose size runs past the file (streamed WAV) counts up to the end of the file.
    const size = Math.min(data[2], b.length - data[1]);
    out.durationSec = size / byteRate;
  }
}

function probeFlacStreamInfo(b: Uint8Array, at: number, out: MutableProbe): void {
  // STREAMINFO body: min/max block (2+2), min/max frame (3+3), then 20 bits rate, 3 bits channels-1, 5 bits bps-1,
  // 36 bits total samples.
  const rate = (u8(b, at + 10) << 12) | (u8(b, at + 11) << 4) | (u8(b, at + 12) >> 4);
  const channels = ((u8(b, at + 12) >> 1) & 7) + 1;
  const total = (u8(b, at + 13) & 0x0f) * 0x100000000 + u32be(b, at + 14);
  if (rate > 0) {
    out.sampleRate = rate;
    out.channels = channels;
    if (total > 0) out.durationSec = total / rate;
  }
}

/** Granule position of the last Ogg page (scanning back from the end), or null. */
function lastOggGranule(b: Uint8Array): number | null {
  for (let i = b.length - 27; i >= 0; i--) {
    if (u8(b, i) === 0x4f && ascii(b, i, 'OggS')) {
      const g = i64le(b, i + 6);
      if (g >= 0) return g;
    }
  }
  return null;
}

function probeOgg(b: Uint8Array, codec: string, out: MutableProbe): void {
  const p = 27 + u8(b, 26);
  const granule = lastOggGranule(b);
  if (codec === 'ogg-vorbis') {
    const channels = u8(b, p + 11);
    const rate = u32le(b, p + 12);
    if (rate > 0) {
      out.channels = channels;
      out.sampleRate = rate;
      if (granule !== null) out.durationSec = granule / rate;
    }
  } else if (codec === 'opus') {
    out.channels = u8(b, p + 9);
    const preSkip = u16le(b, p + 10);
    const inputRate = u32le(b, p + 12);
    out.sampleRate = inputRate > 0 ? inputRate : 48000;
    if (granule !== null) out.durationSec = Math.max(0, granule - preSkip) / 48000;
  } else if (codec === 'ogg-flac') {
    // 0x7F "FLAC" major minor count(2) "fLaC" then the STREAMINFO block header (4).
    const at = p + 13 + 4;
    probeFlacStreamInfo(b, at, out);
    if (granule !== null && out.sampleRate !== undefined) out.durationSec = granule / out.sampleRate;
  }
}

function probeMp3(b: Uint8Array, out: MutableProbe): void {
  let p = id3v2End(b);
  let first = mpegFrame(b, p);
  // Skip a few junk bytes between the tag and the first frame.
  for (let k = 0; first === null && k < 4096 && p < b.length; k++) first = mpegFrame(b, ++p);
  if (first === null) return;
  out.sampleRate = first.sampleRate;
  out.channels = first.channels;
  // Xing / Info frame: side information size depends on version and channel mode.
  const side = first.mpeg1 ? (first.channels === 1 ? 17 : 32) : first.channels === 1 ? 9 : 17;
  const xing = p + 4 + side;
  if ((ascii(b, xing, 'Xing') || ascii(b, xing, 'Info')) && (u32be(b, xing + 4) & 1) !== 0) {
    out.durationSec = (u32be(b, xing + 8) * first.samples) / first.sampleRate;
    return;
  }
  const vbri = p + 4 + 32;
  if (ascii(b, vbri, 'VBRI')) {
    out.durationSec = (u32be(b, vbri + 14) * first.samples) / first.sampleRate;
    return;
  }
  let samples = 0;
  let frame: MpegFrame | null = first;
  while (frame !== null && p + frame.length <= b.length) {
    samples += frame.samples;
    p += frame.length;
    frame = mpegFrame(b, p);
  }
  out.durationSec = samples / first.sampleRate;
}

const ADTS_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

function probeAdts(b: Uint8Array, out: MutableProbe): void {
  let p = id3v2End(b);
  let samples = 0;
  let rate = 0;
  while (p + 7 <= b.length && isAdtsHeader(b, p)) {
    rate = ADTS_RATES[(u8(b, p + 2) >> 2) & 0x0f] ?? 0;
    const channels = ((u8(b, p + 2) & 1) << 2) | (u8(b, p + 3) >> 6);
    if (channels > 0) out.channels = channels;
    const length = ((u8(b, p + 3) & 3) << 11) | (u8(b, p + 4) << 3) | (u8(b, p + 5) >> 5);
    if (length < 7) break;
    samples += ((u8(b, p + 6) & 3) + 1) * 1024;
    p += length;
  }
  if (rate > 0) {
    out.sampleRate = rate;
    out.durationSec = samples / rate;
  }
}

function probeMp4(b: Uint8Array, out: MutableProbe): void {
  const moov = boxes(b, 0, b.length).find(([t]) => t === 'moov');
  if (moov === undefined) return;
  const mvhd = childBox(b, moov, 'mvhd');
  if (mvhd !== undefined) {
    const v1 = u8(b, mvhd[1]) === 1;
    const scale = v1 ? u32be(b, mvhd[1] + 20) : u32be(b, mvhd[1] + 12);
    const duration = v1 ? u64be(b, mvhd[1] + 24) : u32be(b, mvhd[1] + 16);
    if (scale > 0) out.durationSec = duration / scale;
  }
  for (const trak of boxes(b, moov[1], moov[2]).filter(([t]) => t === 'trak')) {
    const mdia = childBox(b, trak, 'mdia');
    if (mdia === undefined) continue;
    const hdlr = childBox(b, mdia, 'hdlr');
    const mdhd = childBox(b, mdia, 'mdhd');
    if (hdlr === undefined || mdhd === undefined || !ascii(b, hdlr[1] + 8, 'soun')) continue;
    const v1 = u8(b, mdhd[1]) === 1;
    const scale = v1 ? u32be(b, mdhd[1] + 20) : u32be(b, mdhd[1] + 12);
    if (scale > 0) out.sampleRate = scale;
  }
  const tkhd = boxes(b, moov[1], moov[2])
    .filter(([t]) => t === 'trak')
    .map((trak) => childBox(b, trak, 'tkhd'))
    .find((t) => t !== undefined && u32be(b, t[2] - 8) > 0);
  if (tkhd !== undefined) {
    // Track width / height are 16.16 fixed point at the end of tkhd.
    out.width = Math.round(u32be(b, tkhd[2] - 8) / 65536);
    out.height = Math.round(u32be(b, tkhd[2] - 4) / 65536);
  }
}

function probeAvif(b: Uint8Array, out: MutableProbe): void {
  const meta = boxes(b, 0, b.length).find(([t]) => t === 'meta');
  if (meta === undefined) return;
  // `meta` is a full box: 4 bytes of version / flags before its children.
  const iprp = boxes(b, meta[1] + 4, meta[2]).find(([t]) => t === 'iprp');
  const ipco = iprp === undefined ? undefined : childBox(b, iprp, 'ipco');
  const ispe = ipco === undefined ? undefined : childBox(b, ipco, 'ispe');
  if (ispe !== undefined) {
    out.width = u32be(b, ispe[1] + 4);
    out.height = u32be(b, ispe[1] + 8);
  }
}

function probeNetpbm(b: Uint8Array, out: MutableProbe): void {
  const head = new TextDecoder('latin1').decode(b.subarray(0, Math.min(b.length, 256)));
  const tokens = head
    .replace(/#[^\n]*/g, ' ')
    .split(/\s+/)
    .filter((t) => t !== '');
  const w = Number(tokens[1]);
  const h = Number(tokens[2]);
  if (Number.isInteger(w) && Number.isInteger(h) && w > 0 && h > 0) {
    out.width = w;
    out.height = h;
  }
}

/** Container metadata of a sniffed file (see the module header). */
export function probeMedia(bytes: Uint8Array, sniff: MediaSniff): MediaProbe {
  const out: MutableProbe = {};
  try {
    switch (sniff.codec) {
      case 'wav':
      case 'wav-unsupported':
        probeWav(bytes, out);
        break;
      case 'flac':
        probeFlacStreamInfo(bytes, 8, out);
        break;
      case 'ogg-vorbis':
      case 'opus':
      case 'ogg-flac':
        probeOgg(bytes, sniff.codec, out);
        break;
      case 'mp3':
      case 'mp2':
        probeMp3(bytes, out);
        break;
      case 'aac':
        probeAdts(bytes, out);
        break;
      case 'm4a':
      case 'mp4':
      case 'mov':
        probeMp4(bytes, out);
        break;
      case 'avif':
        probeAvif(bytes, out);
        break;
      case 'ppm':
        probeNetpbm(bytes, out);
        break;
      default:
        break;
    }
    if (sniff.kind === 'image') {
      const header = sniffImage(bytes);
      if (header !== null) {
        out.width = header.width;
        out.height = header.height;
      }
    }
    if (sniff.codec === 'gif') {
      const delays = gifDelaysMs(bytes);
      if (delays !== null) {
        out.gifFrames = delays.length;
        out.gifDelaysMs = delays;
        out.gifTotalMs = gifTotalMs(delays);
      }
    }
  } catch {
    // Truncated or odd headers leave the fields unknown.
  }
  if (out.durationSec !== undefined && !(Number.isFinite(out.durationSec) && out.durationSec >= 0)) {
    delete out.durationSec;
  }
  return out;
}

/** Asset metadata of a file: the sniffed codec, Phira decodability and the probed container facts. */
export function assetMetaOf(bytes: Uint8Array, sniff: MediaSniff): AssetMeta {
  return { codec: sniff.codec, phiraDecodable: sniff.phiraDecodable, ...probeMedia(bytes, sniff) };
}
